import { useGlobalStore } from '@modules/core/store';
import { IDENTITY_OPERATION } from '@modules/identity/api/const';
import useRequestAccessForBroker from '@modules/identity/hooks/mutations/useRequestAccessForBroker';
import useDecodeHid from '@modules/identity/hooks/mutations/useDecodedHid';
import {
  MappedUser,
  mapTokenToUser,
  setAuthCookies,
  setLocalStorageDataForAccountAndChat,
} from '@modules/identity/utils';
import { identifyAmplitudeSession } from '@shared/analytics/amplitude';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { logEvent } from '@amplitude/analytics-browser';
import { HubException } from '@shared/exceptions';
import queryClient from '@app/queryClient';
import { coreApi } from '@modules/core/api';
import { ClientsRoute } from '@modules/clients/presentation/routes';
import { IdentityRoute } from '@modules/identity/presentation/routes';
import { CURRENT_USER_ROLE } from '@modules/core/utils';
import LoadingOverlay from './components/LoadingOverlay';

const Identity = () => {
  const { hid, next } = IdentityRoute.useSearch();
  const { mutateAsync: mutateRequestAccessForBroker } = useRequestAccessForBroker();
  const { mutateAsync: decodeHuspyId } = useDecodeHid();
  const { setUser } = useGlobalStore();
  const navigate = useNavigate();

  const tryGetBrokerProfileORRequestAccess = async (
    authenticatorId: string,
    tokenUserInfo: MappedUser,
    requestAccessPhoneNumber: string
  ) => {
    try {
      return await queryClient.fetchQuery({
        queryKey: ['brokerProfile', authenticatorId],
        queryFn: () => coreApi.getBrokerByAuthenticatorId(authenticatorId),
      });
    } catch (error: unknown) {
      if (error instanceof HubException && error.statusCode === 403) {
        return await mutateRequestAccessForBroker({
          authenticatorId,
          userProfile: {
            first_name: tokenUserInfo.firstName,
            last_name: tokenUserInfo.lastName,
            email: tokenUserInfo.email,
            phone_number: requestAccessPhoneNumber,
            role: CURRENT_USER_ROLE,
            terms_conditions_version: 'v1',
            accept_terms_conditions: String(true),
          },
        });
      }
    }
    window.location.replace(
      `${IDENTITY_ROUTE}/auth/login-with-pwd?redirectUrl=${window.location.origin}/identity`
    );
  };

  const signupFlow = async (
    authenticatorId: string,
    tokenUserInfo: MappedUser,
    requestAccessPhoneNumber: string
  ) => {
    try {
      await queryClient.fetchQuery({
        queryKey: ['brokerProfile', authenticatorId],
        queryFn: () => coreApi.getBrokerByAuthenticatorId(authenticatorId),
      });
    } catch {
      await mutateRequestAccessForBroker({
        authenticatorId,
        userProfile: {
          first_name: tokenUserInfo.firstName,
          last_name: tokenUserInfo.lastName,
          email: tokenUserInfo.email,
          phone_number: requestAccessPhoneNumber,
          role: CURRENT_USER_ROLE,
          terms_conditions_version: 'v1',
          accept_terms_conditions: String(true),
        },
      });
    }
  };

  const loginFlow = async (
    authenticatorId: string,
    tokenUserInfo: MappedUser,
    requestAccessPhoneNumber: string
  ) => {
    const brokerProfile = await tryGetBrokerProfileORRequestAccess(
      authenticatorId,
      tokenUserInfo,
      requestAccessPhoneNumber
    );
    const { firstName, lastName } = tokenUserInfo;

    setUser({
      ...tokenUserInfo,
      ...brokerProfile,
      name: `${firstName ?? ''} ${lastName ?? ''}`,
      id: tokenUserInfo.username,
      email: tokenUserInfo.email,
      isGrantedAccessToMbPortal: brokerProfile?.is_granted_access_to_mb_portal,
    });

    identifyAmplitudeSession({
      email: tokenUserInfo.email,
      name: `${firstName} ${lastName}`,
      broker_id: tokenUserInfo.username,
      brokerage_id: brokerProfile.brokerage_id ?? '',
    });

    logEvent({ event_type: 'login' });
    setLocalStorageDataForAccountAndChat(tokenUserInfo);
  };

  const processHID = async () => {
    try {
      if (!hid) {
        window.location.href = window.location.origin;
        return;
      }
      const decodedValues = await decodeHuspyId({
        clientType: IDENTITY_CLIENT_ID,
        hid,
      });
      if (decodedValues == null) {
        window.location.href = window.location.origin;
        return;
      }
      const token = String(decodedValues.token);
      const refreshToken = String(decodedValues.refresh_token);
      const operation = String(decodedValues.op);
      const tokenUserInfo = mapTokenToUser(token);
      const requestAccessPhoneNumber = tokenUserInfo.phone_number
        || decodedValues.phone_number
        || '';

      const authenticatorId = tokenUserInfo.username;
      setAuthCookies(token, refreshToken);

      // eslint-disable-next-line unicorn/prefer-ternary
      if (operation === IDENTITY_OPERATION.signUp) {
        await signupFlow(
          authenticatorId,
          tokenUserInfo,
          requestAccessPhoneNumber
        );
      } else {
        await loginFlow(
          authenticatorId,
          tokenUserInfo,
          requestAccessPhoneNumber
        );
      }

      if (next) {
        window.location.href = next;
      } else {
        navigate({ to: ClientsRoute.to });
      }
    } catch (error: any) {
      window.location.href = window.location.origin;
      throw new HubException(
        error?.message ?? 'Hub API error',
        'IdentityError',
        400,
        JSON.stringify(error),
        error
      );
    }
  };

  useEffect(() => {
    processHID();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <LoadingOverlay />;
};

export default Identity;
